/**
@license
Copyright (c) 2023 Advensys LLC. All rights reserved.
*/

import { LitElement, html, css } from 'lit';


import { connect } from 'pwa-helpers/connect-mixin.js';
import { installRouter } from 'pwa-helpers/router.js';
import { updateMetadata } from 'pwa-helpers/metadata.js';

import { SharedStyles } from './shared-styles.js';


// These are the actions needed by this element.
import { store } from '../store.js';
import { navigate,  updateLocationURL } from '../reducers/appSlice.js';
import { signIn, signEnd, fetchUser } from '../reducers/authSlice.js';
import './user-profile.js';
import {storeContext} from './store-context.js';
import {ContextProvider} from '@lit/context';

// import './user-preference.js';

// These are additional elements needed by this element.

import '@material/mwc-drawer';
import '@material/mwc-top-app-bar';

// import "@material/mwc-top-app-bar-fixed";
import '@material/web/progress/linear-progress.js';
import '@material/web/progress/circular-progress.js';
import '@material/web/iconbutton/icon-button.js';
import '@material/web/icon/icon.js';
import '@material/web/menu/menu.js';
import '@material/web/dialog/dialog.js';
import '@material/web/list/list.js';
import '@material/web/button/outlined-button.js';
import '@material/web/button/text-button.js';
import '@material/web/button/elevated-button.js';
import '@material/web/button/filled-button.js';

import '@material/web/select/select-option.js';
import '@material/web/select/outlined-select.js';
import '@material/web/select/filled-select.js';
import '@material/web/checkbox/checkbox.js';
import '@material/web/list/list-item.js';

import '@material/web/textfield/outlined-text-field.js';
import '@material/web/textfield/filled-text-field.js';
import {Task} from '@lit/task';
import '@material/web/menu/menu-item.js';

// These are the icons needed by this element.
import { dataurl } from './paksh-constants.js'  // Import used to separate development urls from production

class PakshApp extends connect(store)(LitElement) {
  static get properties() {
    return {
      appTitle: { type: String },
      _page: { type: String },
      _drawerOpened: { type: Boolean },

      // Additional properties beyond pwa
      _authInitialized: { type: Boolean },
      _user: { type: Object },
      _query: { type: String },
      _feedback: { type: Boolean, state: false },
      _accountUpdate: { type: Boolean },
      _prefUpdate: { type: Boolean, state: false },
      progressHidden: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.progressHidden = true ;
  }

  // Serve as a context provider with an initial level 1 and the color for that
  // level
  _provider = new ContextProvider(this, {
    context: storeContext
  });

  static get styles() {
    return [
      SharedStyles,
      css`
        :host {
          display: block;
          --app-primary-color: #f26b04;
          --app-secondary-color: #293237;
          --app-dark-text-color: var(--app-secondary-color);
          --app-light-text-color: white;
    /*      --app-section-even-color: #f7f7f7; */
          --app-section-even-color: white;  /* set odd even at same color */
          --app-section-odd-color: white;

          --app-header-background-color: white;
          --app-header-text-color: var(--app-dark-text-color);
          --app-header-selected-color: var(--app-primary-color);

          --app-drawer-background-color: var(--app-header-background-color)
          --app-drawer-text-color: var(--app-dark-text-color);
          --app-drawer-selected-color: #78909C;

          --md-sys-color-primary: black;
          --md-sys-color-surface: white

        }


        app-header {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          text-align: center;
          background-color: var(--app-header-background-color);
          color: var(--app-header-text-color);
          border-bottom: 1px solid #eee;
          z-index: 1;
        }

        .toolbar-top {
          background-color: var(--app-header-background-color);
        }

        mwc-top-app-bar {
  --mdc-theme-primary: var(--app-header-background-color);
  --mdc-theme-on-primary: black;
  --mdc-top-app-bar-box-shadow: 4;
  z-index: 1;
}


        [main-title] {
        /*     font-family: 'Pacifico';
          text-transform: lowercase;
          font-size: 30px;
        In the narrow layout, the toolbar is offset by the width of the
          drawer button, and the text looks not centered. Add a padding to
          match that button
          padding-right: 44px; */
        }

        .toolbar-list > a {
          display: inline-block;
          color: var(--app-header-text-color);
          text-decoration: none;
          line-height: 30px;
          padding: 4px 24px;
        }

        .toolbar-list > a[selected] {
          color: var(--app-header-selected-color);
          border-bottom: 4px solid var(--app-header-selected-color);
        }

        .drawer-list {
          box-sizing: border-box;
          width: 100%;
          height: 100%;
          padding: 24px;
          background: var(--app-drawer-background-color);
          position: relative;
        }

        .drawer-list > a {
          display: block;
          text-decoration: none;
          color: var(--app-drawer-text-color);
          line-height: 40px;
          padding: 0 24px;
        }

        .drawer-list > a[selected] {
          color: var(--app-drawer-selected-color);
        }


        nav a:hover {
            color: #f26b04;
            text-decoration: none;
        }

        .userimage {
          height: 32px;
          border-radius: 50%;
        }

        /* Workaround for IE11 displaying <main> as inline */
        main {
          display: block;
        }

        .main-content {
          padding-top: 12px;
          min-height: 100vh;
        }

        .page {
          display: none;
        }

        .page[active] {
          display: block;
        }

         .logo {
          font-size: 36px;
          color: #f26b04;
          padding-left: 10px;
          text-decoration: none;
        }

        .logo:hover {
          text-decoration: none;
        }

        .logo :visited {
          color: #f26b04;
          text-decoration: none;
        }

        .drawericon {
          width: 24px;
          height: 24px;
          padding-right: 10px;
          float: left;
        }

        .alignrighticon {
          width: 24px;
          height: 24px;
          padding-right: 10px;
          float: right;
        }

        footer {
          padding: 24px;
          background: var(--app-light-text-color);
          color: var(--app-dark-text-color);
          text-align: center;
        }

        footer a {
          color: var(--app-dark-text-color);
          text-decoration: none;
        }

        footer a:hover {
          text-decoration: underline;
        }

        mwc-drawer {
          z-index: 2;
        }

        .copyright {
          font-size: 12px;
        }


        @media (max-width: 500px) {
          .icon-only {
            display: none;
          }

          .toolbar-list > a {
          padding: 4px 12px;
        }


        }

        @media (min-width: 460px) {
        :host {
            padding-top: 0px;
          }
        }

        /* small screen */
        @media (max-width: 767px) {
          :host {
             padding-top: opx;
          }

          [main-title] {
            padding-right: 0px;
          }

        }

        .profileBar {
      /*      border-top: 1px solid #eee; */
            height: 64px;
            padding: 0 32px 0 0;
            display: inline-block;
        }


      .material-icons-new {
    display: inline-block;
    width: 24px;
    height: 24px;
    background-repeat: no-repeat;
    background-size: contain;
}

.icon-white {
    webkit-filter: contrast(4) invert(1);
    -moz-filter: contrast(4) invert(1);
    -o-filter: contrast(4) invert(1);
    -ms-filter: contrast(4) invert(1);
    filter: contrast(4) invert(1);
}

  md-icon-button.large {
    --md-icon-button-icon-size: 32px
  }

  .row {
        display: flex;
        align-items: flex-start;
        vertical-align: middle;
        justify-content: space-between;
      }

      mwc-drawer[open] top-app-bar {
  /* Default width of drawer is 256px. See CSS Custom Properties below */
  --mdc-top-app-bar-width: calc(100% - var(--mdc-drawer-width, 256px));
}
      .align-center {
        display: flex;
        justify-content: center;
      }
      `
    ];
  }

  render() {
    // Anything that's related to rendering should be done in here.
    return html`
     ${!!this._user || this._page === 'public' || this._page === 'signup' ||
        this._page === 'about' || this._page === 'terms' || this._page === 'privacy' ?
        html`<div style="display:${this.progressHidden ? 'none' : '' }"><md-linear-progress indeterminate></md-linear-progress></div>` : html`<div style="display:${this.progressHidden ? '' : 'none' }"><md-linear-progress indeterminate></md-linear-progress></div>`}
      <!-- Header -->
      ${this._page === 'signup'  ?
        html`
        <mwc-top-app-bar dense>
          <a class="logo" href="/public" slot="navigationIcon"><div class="logo">पaksh</div></a>
        </mwc-top-app-bar>
        ` :
        html`
      <mwc-drawer hasHeader type="modal" .open="${ this._drawerOpened}">
          <div>
            <nav class="drawer-list" ?hidden="${!this._user}">
              <a  ?selected="${this._page === 'home'}" href="/home"><md-icon class="drawericon">home</md-icon>Home</a>
              <a ?selected="${this._page === 'judgelist'}" href="/judgelist?q=${this._query}"><md-icon class="drawericon">gavel</md-icon>Judges</a>
              <a ?selected="${this._page === 'lawyerlist'}" href="/lawyerlist?q=${this._query}"><md-icon class="drawericon">person_apron</md-icon>Lawyers</a>
              <hr>
              <a ?selected="${this._page === 'help'}" href="/help"><md-icon class="drawericon">help_outline</md-icon>Help</a>
            </nav>
          </div>
        <div slot="appContent">
          <mwc-top-app-bar centerTitle>
              ${this._user ?
                html`<md-icon-button  icon='menu'   slot="navigationIcon" @click="${() => this._drawerOpenedChanged()}"><md-icon>menu</md-icon></md-icon></md-icon-button>`
               :
                html` <a class="logo" href="/public" slot="navigationIcon"><div class="logo">पaksh</div></a>`
              }
              <!-- This gets hidden on a small screen-->


              <div style="position: relative;" slot="actionItems">
                ${this._user && this._user.photoUrl ?
                  html`<md-icon-button  @click="${() => this._feedback = true}"><md-icon>feedback</md-icon>
                  </md-icon-button>
                  <md-icon-button class="large" id="user-anchor" @click="${() => this._filterButtonClicked()}" >
                  <md-icon><img class="userimage" src="${this._user.photoUrl}" alt="user image"></md-icon></md-icon-button>`
                  : html`<md-text-button  @click="${() => store.dispatch(signIn())}">Log In</md-text-button>`}
                    <md-menu id="acctmenu" relative x="-40" y="50"   anchor="user-anchor">
                      <md-menu-item><div slot="headline" type="option" @click="${() => this._accountActions(0)}">Edit Profile</div></md-menu-item>
                      <!--<md-menu-item><div slot="headline">Edit Preferences</div></md-menu-item>-->
                      <md-divider></md-divider>
                      <md-menu-item><div slot="headline" type="option" @click="${() => this._accountActions(1)}">Sign Out</div></md-menu-item>
                    </md-menu>
              </div>
              ${!this._user && this._page === 'public' ?
                html` <div style="position: relative;" slot="actionItems">
                        <md-text-button aria-label="Sign Up"
                          @click="${() => window.location.href = '/signup'}">Sign Up</md-text-button>
                      </div>`
                : html``}

            <nav class="toolbar-list" ?hidden="${!this._user}" slot="title">
              <a ?selected="${this._page === 'home'}" href="/home?q=${this._query}"><md-icon class="drawericon">home</md-icon><span class="icon-only">Home</span></a>
              <a ?selected="${this._page === 'judgelist'}" href="/judgelist?q=${this._query}"><md-icon class="drawericon">gavel</md-icon><span class="icon-only">Judges</span></a>
              <a ?selected="${this._page === 'lawyerlist'}" href="/lawyerlist?q=${this._query}"><md-icon class="drawericon">person_apron</md-icon><span class="icon-only">Lawyers</span></a>
            </nav>
          </mwc-top-app-bar>
        `}
      <!-- Drawer content -->

      <!-- Main content -->
      <main role="main" class="main-content">

        <md-dialog .open=${this._feedback} @closed="${() => {this._feedback = false}}" >
          <div slot="headline" class="row">
            Feedback
            <md-icon-button form="form-id" value="cancel"><md-icon>close</md-icon></md-icon-button>
          </div>
          <form slot="content" id="form-id" method="dialog">
            <md-outlined-text-field dialogInitialFocus label='Enter feedback' id='feedbacknote' icon="edit_note" value='' helper="Thank you we at पaksh greatly appreciate your input, it helps us improve the product to further meet your needs" > </md-outlined-text-field>
          </form>
          <div slot="actions">
            <md-text-button form="form-id" @click="${() => this._feedbackSubmit()}">Submit</md-text-button>
          </div>
        </md-dialog>


        <md-dialog id="profiledialog" .open=${this._accountUpdate}  @closed="${() => {this._accountUpdate = false}}" hideActions>
          <div slot="headline" class="row">
            Update your profile
            <md-icon-button form="profile-form-id" value="cancel"><md-icon>close</md-icon></md-icon-button>
          </div>
          <form slot="content" id="profile-form-id" method="dialog">
            <user-profile .data=${this._profiledata}></user-profile>
          </form>
          <div slot="actions">
            <md-text-button form="profile-form-id" @click="${() => this._profileSubmit()}">Submit</md-text-button>
          </div>
        </md-dialog>

        <md-dialog id="perfdialog" .open=${this._prefUpdate} @closed="${() => {this._prefUpdate = false}}" hideActions>
          <div slot="headline" class="row">
            Update your preferences
            <md-icon-button form="pref-form-id" value="cancel"><md-icon>close</md-icon></md-icon-button>
          </div>
          <form slot="content" id="perf-form-id" method="dialog">
            <user-preference></user-preference>
          </form>
          <div slot="actions">
            <md-text-button form="perf-form-id" @click="${() => this._perfSubmit()}">Submit</md-text-button>
          </div>
        </md-dialog>

          <div ?hidden="${!this._user}">
            <paksh-home @progress=${this.updateProgress} class="page" ?active="${this._page === 'home' && this._user}"></paksh-home>
            <judge-list class="page" ?active="${this._page === 'judgelist' && this._user}"></judge-list>
            <judge-detail class="page" ?active="${this._page === 'judgedetail' && this._user}"></judge-detail>
            <lawyer-list class="page" ?active="${this._page === 'lawyerlist' && this._user}"></lawyer-list>
            <lawyer-detail class="page" ?active="${this._page === 'lawyerdetail' && this._user}"></lawyer-detail>
            <legalcase-detail class="page" ?active="${this._page === 'legalcasedetail' && this._user}"></legalcase-detail>
            <paksh-help class="page" ?active="${this._page === 'help' && this._user}"></paksh-help>
          </div>
          <div>
            <paksh-public class="page" ?active="${this._page === 'public'}"></paksh-public>
            <paksh-terms class="page" ?active="${this._page === 'terms'}"></paksh-terms>
            <paksh-privacy class="page" ?active="${this._page === 'privacy'}"></paksh-privacy>
            <paksh-about class="page" ?active="${this._page === 'about'}"></paksh-about>
            <paksh-signup class="page" ?active="${this._page === 'signup'}"></paksh-signup>
            <paksh-view404 class="page" ?active="${this._page === 'view404'}"></paksh-view404>
          </div>
        </main>

      <footer id="footer" name="footername">
            <a ?selected="${this._page === 'terms'}" href="/terms">Terms</a>
            <span> | </span>
            <a ?selected="${this._page === 'privacy'}" href="/privacy">Privacy</a>
            <span> | </span>
            <a ?selected="${this._page === 'about'}" href="/about">About</a>
            <p>
              <div class="copyright">&copy; All Rights Reserved Advensys LLC</div>
            </p>
      </footer>

      </div>
    </mwc-drawer>

    `;
  }


  // ---------------------------------
  //
  // ----------------------------------
  firstUpdated() {
    installRouter((location) => store.dispatch(navigate(location)));
    this._accountUpdate = false;
    // get authenticated user
    //  if (location.pathname != "/about" ) {   //  GET RID LATER DONE TO AVOID CALLS TO FIREBASE
    store.dispatch(fetchUser());
    //  }
  }

  // ---------------------------------
  //
  // ----------------------------------
  updated(changedProps) {
    if (changedProps.has('_page')) {
      const pageTitle = `${this.appTitle  } - ${  this._page}`;
      updateMetadata({
        title: pageTitle,
        description: pageTitle
        // This object also takes an image property, that points to an img src.
      });
      window.scrollTo(0, 0);
    }

    if (changedProps.has('_user') && (this._page == "public" || this._page == "signup") && this._user != null) {  // if user logged and current page is public go to home
      store.dispatch(updateLocationURL('/home'))
    }

    if (changedProps.has('_user') && this._user == null) {  // if user logged and current page is public go to home
      // store.dispatch(updateLocationURL('/public'))
    }
  }


//---------------------
updateProgress(e) {
  this.progressHidden = e.detail;
  }
//-------------------------

  // ---------------------------------
  //
  // ----------------------------------
  _accountActions(action) {
    const acctmenu= this.shadowRoot.querySelector('#acctmenu')
    switch (action) {
      default:
        break;
      case 0:
        this._accountUpdate = true;
        break;
      case 1:
        acctmenu.open = !acctmenu.open;
        store.dispatch(signEnd());
        break;
      case 2:
        acctmenu.open = !acctmenu.open;
        this._prefUpdate = true;
        break;
    }
    // this.shadowRoot.querySelector('#acctmenu').select(-1);
  }

  // ---------------------------------
  //
  // ----------------------------------
  _feedbackButtonClicked() {
    this._feedback = true;
  }

  // ---------------------------------
  //
  // ----------------------------------
  _feedbackSubmit(e) {
    let data = {
      "feedback": this.shadowRoot.querySelector('#feedbacknote').value,
      "page": this._page
    }
    // console.log(data)
    let token = window.localStorage.getItem('token');
    fetch(`${dataurl}/feedback`,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': token
        },
        method: "POST",
        body: JSON.stringify(data)
      })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          // console.error("Fail")
        }
      });
    // this._feedback=false;
    this.shadowRoot.querySelector('#feedbacknote').value = '';
  }

  // ---------------------------------
  //
  // ----------------------------------
  _filterButtonClicked() {
    this.shadowRoot.querySelector('#acctmenu').open = true;

  }

  // ---------------------------------
  //
  // ----------------------------------
_drawerOpenedChanged(e) {
  this._drawerOpened = !this._drawerOpened
  }


  // ---------------------------------
  //  TODO:  Fix this.query to select court based on user profile
  //  TODO:  Fix this.query to handle lawyers query also
  // ----------------------------------
  stateChanged(state) {

    this._page = state.app.page;
    this._authInitialized = state.auth.firebaseinitialized;
    this._user = state.auth.user;
    this._query = state.judges && state.judges.query || 'h-mh';   // this has to be tweaked for court
  }

}

window.customElements.define('paksh-app', PakshApp);
